import ACTIVATION_ERRORS_DATA from '../../consts/analytics/activationErrorsData';
import * as dataCollectionTypes from '../../consts/uam/dataCollectionTypes';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import * as yapWebSocketActionTypes from '../../consts/yap/yapWebSocketActionTypes';
import * as yapWebSocketEventTypes from '../../consts/yap/yapWebSocketEventTypes';
import {setDeviceActivationStatus} from '../../state/ducks/iotDevice/actions';
import {
    addYapLastDataCollectionTime,
    setYapActivationInProgress,
    setYapAutoCleaningInProgress,
    setYapRequestActionTypeData,
    setYapSocketAlive,
    setYapUpdateSettingsInProgress,
} from '../../state/ducks/yapEncrypted';
import {selectIsDiagnosticFlow} from '../../state/selectors/advancedDiagnostic';
import {selectIsDeviceReady} from '../../state/selectors/iotDevice';
import {
    makeSelectIsHolderByAssetId,
    makeSelectIsYapFindDeviceInProgress,
    makeSelectRequestActionTypeData,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import converter from '../../utils/converter';
import {amplitudeADTracking, amplitudeDataCollectionTracking, amplitudeSettingsTracking} from '../amplitude';
import analyticsService from '../analyticsService';
import appErrorService from '../app/appErrorService';
import log from '../logger/log';
import {getErrorMapping} from '../mapping/yapMappings';
import advanceDiagnosticService from '../yap/advanceDiagnosticService';
import yapConnectionService from '../yap/yapConnectionService';
import yapFWUService from '../yap/yapFWUService';
import yapService from '../yap/yapService';
import yapStateService from '../yap/yapStateService';
import * as dataCollectionService from './dataCollectionService';
import uamClientService from './uamClientService';
import uamService from './uamService';

const PONG = 'pong';
const proceedMessage = async (message, isHolder) => {
    const data = converter.stringToJson(message.data) || {};
    const {eventType, request, error, assetId, status, requestId, isSBLMode, events, response, associatedDevices} =
        data || {};

    if (response === PONG) {
        dispatch(setYapSocketAlive(true, isHolder));
        return;
    }

    const uamError = getErrorMapping(error);

    switch (eventType) {
        case yapWebSocketEventTypes.CONNECTION_STATUS_CHANGE:
            const {engine} = associatedDevices || {};

            yapConnectionService.proceedConnectionChange(status, assetId, isSBLMode, engine);

            break;
        case yapWebSocketEventTypes.INFERENCE_COMPLETED:
            const {inferenceName} = data || {};

            await proceedInferenceCompleted(assetId, inferenceName);
            break;
        case yapWebSocketEventTypes.ASYNC_REQUEST:
            const {action, value} = request || {};
            const actionStatus = yapService.proceedAction({
                status,
                requestId,
                assetId,
                isSync: action === yapWebSocketActionTypes.REFRESH_INFO,
                isDataCollection: yapWebSocketActionTypes.isDataCollection(action),
            });

            if (!actionStatus && action !== yapWebSocketActionTypes.PROGRESS) return;

            switch (action) {
                case yapWebSocketActionTypes.ACTIVATE:
                    await proceedUamActivationAction(actionStatus, true);
                    break;
                case yapWebSocketActionTypes.DEACTIVATE:
                    await proceedUamActivationAction(actionStatus, false);
                    break;
                case yapWebSocketActionTypes.REFRESH_INFO:
                    await yapConnectionService.proceedRefreshInfo(actionStatus, assetId);
                    break;
                case yapWebSocketActionTypes.RUN_COMMAND:
                    const {actionType} = makeSelectRequestActionTypeData()(getState());

                    switch (actionType) {
                        case yapRunCommandActionTypes.FIND_DEVICE:
                            await proceedFindDevice(actionStatus, new Date());
                            break;
                        case yapRunCommandActionTypes.CLEAN_DEVICE:
                            await proceedCleanDevice(actionStatus, uamError);
                            break;
                        default:
                            break;
                    }
                    break;
                case yapWebSocketActionTypes.DATA_COLLECTION:
                case yapWebSocketActionTypes.COLLECT_DATA:
                    await proceedSynchronizeDataCollection(actionStatus, assetId);
                    break;
                case yapWebSocketActionTypes.UPDATE_SETTING:
                    updateDeviceSettings(actionStatus, assetId);
                    break;
                case yapWebSocketActionTypes.PROGRESS:
                    yapFWUService.updateFirmwareProgress(assetId, value);
                    break;
                case yapWebSocketActionTypes.FIRMWARE_UPGRADE:
                    await yapFWUService.proceedFWU(actionStatus, assetId, uamError);
                    break;
                case yapWebSocketActionTypes.RESET_DEVICE:
                    await proceedResetDevice(actionStatus, assetId);
                    break;
                default:
                    break;
            }
            break;
        case yapWebSocketEventTypes.DEVICE_EVENT:
            if (!events) return;

            events.forEach((event) => {
                yapStateService.updateDeviceCharacteristics(event);
            });
            break;
        default:
            break;
    }
};

const proceedSynchronizeDataCollection = async (status, assetId) => {
    const state = getState();
    const isDeviceReady = selectIsDeviceReady(state);
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);
    const dataCollectionType = dataCollectionService.getCurrentDataCollectionType();

    if (status === yapActionStatusTypes.SUCCESS) {
        dataCollectionService.setDataCollectionFlowReceived({isReceived: true, isHolder, dataCollectionType});
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);

        //for diagnostic data collection flow should wait for INFERENCE_COMPLETED event
        if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
            advanceDiagnosticService.checkDiagnosticResult(isHolder);
            return;
        }

        if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_GLOBAL) {
            amplitudeDataCollectionTracking.trackGlobalDataCollectionCompleted(isHolder);
        }

        dispatch(addYapLastDataCollectionTime());
    } else if (status === yapActionStatusTypes.CANCELED) {
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);

        if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_GLOBAL) {
            amplitudeDataCollectionTracking.trackGlobalDataCollectionCompleted(isHolder);
        }
    } else if (isDeviceReady) {
        await uamClientService.proceedSynchronizeAllData(isHolder, true, dataCollectionType);
    } else {
        if (dataCollectionType === dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC) {
            advanceDiagnosticService.setDiagnosticFailed(isHolder);
            return;
        }
        dataCollectionService.setDataCollectionFlowInProgress(false, dataCollectionType);
    }
};

const proceedInferenceCompleted = async (assetId, inferenceName) => {
    const isAdvanceDiagnostic = selectIsDiagnosticFlow(getState());

    if (!isAdvanceDiagnostic) return;

    const isHolder = makeSelectIsHolderByAssetId(assetId)(getState());
    const dataCollectionType = dataCollectionService.getCurrentDataCollectionType();
    const isReceived = dataCollectionService.getIsDataCollectionReceived(
        dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC,
        isHolder
    );

    if (dataCollectionType !== dataCollectionTypes.DATA_COLLECTION_DIAGNOSTIC && !isReceived) return;

    if (!inferenceName) return advanceDiagnosticService.setInferenceCompleted(isHolder);

    const diagnosticData = await uamClientService.getInferenceInfo(assetId, inferenceName);

    if (!diagnosticData) return advanceDiagnosticService.setInferenceCompleted(isHolder);

    const logData = JSON.stringify(diagnosticData);

    log.info(
        `InferenceCompleted for isHolder: ${isHolder} inferenceName: ${inferenceName} assetId: ${assetId} with data: ${logData}`
    );

    advanceDiagnosticService.clearFailedAttempts(isHolder);

    const {result: diagnosticResult} =
        diagnosticData?.data?.items?.find((x) => x.inferenceName === inferenceName) || {};
    const {L0 = null, L1 = null, device_state} = diagnosticResult ? converter.stringToJson(diagnosticResult) || {} : {};

    let deviceState = device_state ? device_state.toLowerCase() : null;
    let inferenceCode = L0;
    let inferenceCode_L1 = L1;

    const isSuccessFlowForInferenceCode = advanceDiagnosticService.getIsSuccessFlowForInferenceCode(
        isHolder,
        deviceState,
        inferenceCode
    );

    if (isSuccessFlowForInferenceCode) {
        //if success flow reset error code
        inferenceCode = null;
        inferenceCode_L1 = null;
        deviceState = null;
    }

    const isMaxSuccessfulAttempts = advanceDiagnosticService.checkMaxSuccessfulAttempts(isHolder, inferenceCode);

    if (isMaxSuccessfulAttempts) {
        //if the maximum number of attempts is exceeded - set a success flow (null)
        inferenceCode = null;
        inferenceCode_L1 = null;
        deviceState = null;
    }

    advanceDiagnosticService.setDiagnosticPartResult({deviceState, inferenceCode, inferenceCode_L1, isHolder});
    if (inferenceCode) {
        amplitudeADTracking.trackADCompleted({isHolder, inferenceCode});
    }
    advanceDiagnosticService.checkDiagnosticResult(isHolder);
};

const proceedResetDevice = (status, assetId) => {
    const state = getState();
    const isHolder = makeSelectIsHolderByAssetId(assetId)(state);

    dispatch(
        setYapRequestActionTypeData({
            isHolder,
            actionType: yapRunCommandActionTypes.RESET_DEVICE,
            newValue: {status},
        })
    );

    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

const proceedFindDevice = async (status, startVibrateTS) => {
    dispatch(setYapUpdateSettingsInProgress(false));
    const isYapFindDeviceInProgress = makeSelectIsYapFindDeviceInProgress()(getState());
    const isSuccess = status === yapActionStatusTypes.SUCCESS;

    amplitudeSettingsTracking.trackLocateMyDeviceCompleted(isSuccess);

    if (isSuccess) {
        dispatch(
            setYapRequestActionTypeData({actionType: yapRunCommandActionTypes.FIND_DEVICE, newValue: startVibrateTS})
        );

        if (isYapFindDeviceInProgress) {
            log.info(
                `uamWebSocketResponseService: ${yapRunCommandActionTypes.FIND_DEVICE} process started. Status: ${status}`
            );
        } else {
            log.info(`uamWebSocketResponseService: End ${yapRunCommandActionTypes.FIND_DEVICE} process`);
        }
    } else {
        appErrorService.showDeviceSettingsGlobalError();
    }
};

const proceedCleanDevice = async (status, error) => {
    dispatch(
        setYapRequestActionTypeData({
            actionType: yapRunCommandActionTypes.CLEAN_DEVICE,
            newValue: {status, errorCode: error?.errorCode},
        })
    );

    log.info(
        `uamWebSocketResponseService: End cleanDevice process. Status: ${status}; ${
            !!error ? 'error: ' + JSON.stringify(error) : ''
        }`
    );

    dispatch(setYapAutoCleaningInProgress(false));
};

const updateDeviceSettings = (status, assetId) => {
    const isHolder = makeSelectIsHolderByAssetId(assetId)(getState());

    if (status === yapActionStatusTypes.FAILED) {
        appErrorService.showDeviceSettingsGlobalError();
    } else {
        yapStateService.updateDeviceSettings({isHolder, isNeedInformUser: true});
    }

    dispatch(setYapUpdateSettingsInProgress(false, isHolder));
};

const proceedUamActivationAction = async (status, isActivateAction) => {
    if (status === yapActionStatusTypes.SUCCESS) {
        dispatch(setDeviceActivationStatus(isActivateAction));
    } else {
        analyticsService.pushActivationFailedError(ACTIVATION_ERRORS_DATA.ERROR_ACTIVATION_FAILED);
        await uamService.checkDeviceActivationStatus();
    }
    dispatch(setYapActivationInProgress(false));
};

export default {
    proceedCleanDevice,
    proceedFindDevice,
    proceedMessage,
    proceedResetDevice,
    proceedSynchronizeDataCollection,
    proceedUamActivationAction,
    updateDeviceSettings,
};
